;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { createContext, ReactNode, Suspense, use, useContext } from 'react'

export type RumData = Record<string, unknown>

const RumDataContext = createContext<RumData | undefined>(undefined)

export function useRumData(additionalData?: RumData) {
  // We deliberately aren't throwing if the context is not found
  // because not every app will have this provider
  const parentValue = useContext(RumDataContext)
  if (additionalData) {
    return { ...parentValue, ...additionalData }
  }
  return parentValue
}

/**
 * Provide data that will be attached to any RUM events triggered under this provider
 * Any synchronous data will be attached to all RUM events triggered under this provider
 * Any asynchronous data will be attached to any RUM events triggered under this provider after the data is ready
 * Nested providers will merge their values with the parent's
 * Children that provide the same key will override the parent's value
 * */
export function RumDataProvider({
  children,
  value: valueSync = {},
  valuePromise,
}: {
  children: ReactNode
  /**
   * Contextual data that will be attached to any RUM events triggered under this provider
   * Nested providers will merge their values with the parent's
   * Children that provide the same key will override the parent's value
   *  */
  value?: RumData
  /**
   * Async contextual data that will be attached to any RUM events triggered under this provider
   * If events are triggered before the data is ready, the data will be ignored and only synchronous data will be used
   *
   * Nested providers will merge their values with the parent's
   * Children that provide the same key will override the parent's value
   *  */
  valuePromise?: Promise<RumData>
}) {
  // Collect together any parent data plus any synchronous data provided by this provider
  const mergedValue = useRumData(valueSync)

  return (
    <Suspense
      fallback={
        <RumDataSuspenseFallback value={mergedValue}>
          {children}
        </RumDataSuspenseFallback>
      }
    >
      <RumDataProviderBlocking value={mergedValue} valuePromise={valuePromise}>
        {children}
      </RumDataProviderBlocking>
    </Suspense>
  )
}

function RumDataProviderBlocking({
  value: valueSync = {},
  valuePromise,
  children,
}: {
  value?: RumData
  valuePromise?: Promise<RumData>
  children: ReactNode
}) {
  // Enriches the synchronous data with the asynchronous data
  const value = Object.assign({}, valueSync)
  if (valuePromise) {
    Object.assign(value, use(valuePromise))
  }

  return <RumDataContext value={value}>{children}</RumDataContext>
}

/**
 * Just provides the synchronous data while the async data is loading
 * Allows the rest of the app to continue rendering, and not block on tracking metadata
 */
function RumDataSuspenseFallback({
  value,
  children,
}: {
  value?: RumData
  children: ReactNode
}) {
  return <RumDataContext value={value}>{children}</RumDataContext>
}

type RumActionFunction = (actionName: string, data?: RumData) => void

const RumActionFunctionContext = createContext<RumActionFunction | undefined>(
  undefined,
)

export function useRumActionFunction() {
  return useContext(RumActionFunctionContext)
}

/**
 * Wire up a function that will be called when an action is triggered
 *
 * @example
 *
 * <RumActionFunctionProvider onAction={(actionName, data) => {
 *   trackAction(actionName, data)
 * }}>
 *   <App/>
 * </RumActionFunctionProvider>
 */
export function RumActionFunctionProvider({
  children,
  onAction,
}: {
  children: ReactNode
  /**
   * Function that will be called when an action is triggered
   * @param actionName - The name of the action
   * @param data - The data associated with the action
   */
  onAction: RumActionFunction
}) {
  return (
    <RumActionFunctionContext.Provider value={onAction}>
      {children}
    </RumActionFunctionContext.Provider>
  )
}

export type RumProps = {
  /**
   * The name that will appear in RUM actions when interacting with this component
   * The event type will be prepended to the action name: `${eventType} on ${actionName}`
   * */
  rumActionName?: string
  /**
   * Any additional data that will be added to the RUM action.
   * This will be merged with any contextual data provided by `<RumDataProvider>`s
   */
  rumData?: RumData
}

/**
 * Returns a callback function to trigger a RUM action
 *
 * Only call this from an event handler (e.g. `onClick`)
 * Do not use in render, or in useEffect as the function reference is not stable
 */
export function useTrackRumAction() {
  const actionFunction = useRumActionFunction()
  const parentData = useRumData()

  return function trackRumAction(
    actionName: string,
    eventType: 'click' | 'tooltip view',
    data?: RumData,
  ) {
    if (actionFunction) {
      actionFunction(`${eventType} on ${actionName}`, {
        ...parentData,
        ...data,
      })
    }
  }
}
